import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import kitchenerWaterlooConstruction from "@/assets/kitchener-waterloo-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const KitchenerWaterloo = () => {
  const cityData = {
    name: "Kitchener-Waterloo",
    region: "Waterloo Region",
    population: "570K",
    slug: "kitchener-waterloo-general-contracting",
    intro: "The Kitchener-Waterloo region is Ontario's tech hub, driving demand for modern office, residential, and mixed-use construction. Blue Knight Contracting supports innovation and growth with tailored construction services.",
    featured_services: [
      "Tech Office Construction",
      "Innovation Centers",
      "Mixed-Use Development",
      "Residential Growth"
    ],
    nearby_areas: ["Cambridge", "Guelph", "Stratford", "Brantford"],
    cta: "Build your future in Kitchener-Waterloo with Blue Knight."
  };

  const faqData = [
    {
      question: "Do you specialize in tech office construction in Kitchener-Waterloo?",
      answer: "Yes, we specialize in modern tech office construction including innovation labs, collaborative workspaces, data centers, and technology campuses throughout the Kitchener-Waterloo corridor."
    },
    {
      question: "Can you build innovation centers and research facilities?",
      answer: "Absolutely. We have extensive experience constructing innovation centers, research facilities, incubators, and technology parks for universities and private companies in the region."
    },
    {
      question: "What types of mixed-use developments do you build?",
      answer: "We construct mixed-use developments combining residential, commercial, and office spaces, including transit-oriented developments and live-work communities in Kitchener-Waterloo."
    },
    {
      question: "What areas around Kitchener-Waterloo do you serve?",
      answer: "We serve the entire Waterloo Region including Cambridge, Guelph, Stratford, Brantford, and surrounding communities in the technology corridor."
    },
    {
      question: "Do you work with universities and tech companies?",
      answer: "Yes, we have experience working with the University of Waterloo, Wilfrid Laurier University, and numerous tech companies on campus expansions and corporate facilities."
    },
    {
      question: "Can you handle rapid residential growth projects?",
      answer: "Yes, we're equipped to handle large-scale residential developments, condominiums, and housing projects to support the region's rapid population and employment growth."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting Kitchener-Waterloo",
    "description": "Professional general contracting and construction services in Kitchener-Waterloo and Waterloo Region. Specializing in tech office construction, innovation centers, and mixed-use development.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Kitchener-Waterloo",
      "addressRegion": "Ontario",
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/kitchener-waterloo",
    "areaServed": {
      "@type": "City",
      "name": "Kitchener-Waterloo"
    },
    "serviceType": [
      "General Contracting Kitchener-Waterloo",
      "Tech Office Construction",
      "Innovation Centers",
      "Mixed-Use Development",
      "Residential Growth"
    ]
  };

  const constructionServices = [
    {
      title: "Tech Office Construction",
      description: "Modern office spaces designed for technology companies and innovation-focused businesses.",
      icon: Building,
      features: [
        "Technology campuses",
        "Innovation labs",
        "Collaborative workspaces",
        "Data centers",
        "Clean room facilities",
        "Smart building systems"
      ]
    },
    {
      title: "Innovation Centers",
      description: "Specialized facilities for research, development, and technology incubation.",
      icon: HardHat,
      features: [
        "Research facilities",
        "Technology incubators",
        "University partnerships",
        "Startup centers",
        "Maker spaces",
        "Conference facilities"
      ]
    },
    {
      title: "Mixed-Use Development",
      description: "Integrated developments combining residential, commercial, and office spaces.",
      icon: Home,
      features: [
        "Live-work communities",
        "Transit-oriented development",
        "Urban villages",
        "Retail integration",
        "Student housing",
        "Commercial plazas"
      ]
    },
    {
      title: "Residential Growth",
      description: "Housing developments to support the region's rapid population and employment growth.",
      icon: Wrench,
      features: [
        "Condominium buildings",
        "Residential subdivisions",
        "Affordable housing",
        "Student residences",
        "Executive housing",
        "Multi-family developments"
      ]
    }
  ];

  return (
    <>
      <SchemaMarkup type="LocalBusiness" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Kitchener-Waterloo General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in Kitchener-Waterloo
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get KW Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Tech Hub Specialists</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={kitchenerWaterlooConstruction} 
                  alt="Tech office buildings under construction in Kitchener-Waterloo" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Kitchener-Waterloo Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in Kitchener-Waterloo
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From tech office construction to innovation centers, we deliver cutting-edge construction services for Canada's technology corridor.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Kitchener-Waterloo Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services for Ontario's innovation capital and technology corridor.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in Kitchener-Waterloo and Waterloo Region.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Waterloo Region Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving Kitchener-Waterloo and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout Waterloo Region and the technology corridor.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Kitchener-Waterloo</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Kitchener-Waterloo Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in Kitchener-Waterloo and Waterloo Region.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Kitchener-Waterloo Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from the technology corridor's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">KW & Waterloo Region</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get KW Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our KW Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default KitchenerWaterloo;